import type { StorageValue } from "zustand/middleware";

import { createImmerStore } from "@expert/state-utils";

import type { RightTopTab } from "@/sdk/agent/chat/rightTopTab";
import type { AutopilotState, ChatMessage, NeglectionState, SpellCheckState } from "@/sdk/agent/chat/types";

type ChatId = string;
export type ChatRightBottomTab = "a" | "b";

/** State for persisted chat store data */
interface PersistedChatState {
    chatNeglectionState: Map<ChatId, NeglectionState>;
    chatAutopilotState: Map<ChatId, AutopilotState>;
    closeReason: Map<ChatId, string>;
}

type MessageId = string;

interface ChatState {
    readonly activeChatId: ChatId | undefined;
    readonly typingCustomerChatIds: Set<ChatId>;
    readonly chatNeglectionState: Map<ChatId, NeglectionState>;
    readonly chatAutopilotState: Map<ChatId, AutopilotState>;
    readonly closeReason: Map<ChatId, string>;
    /** Spellcheck state per chat - not persisted */
    readonly chatSpellCheckState: Map<ChatId, SpellCheckState>;
    /** Right-bottom panel tab selection per chat (Assist=a, Flows=b) - not persisted */
    readonly chatRightBottomTab: Map<ChatId, ChatRightBottomTab>;
    /** Right-top panel tab selection per chat (redesigned layout) - not persisted */
    readonly chatRightTopTab: Map<ChatId, RightTopTab>;
    setActiveChatId: (chatId: ChatId) => void;
    clearActiveChatId: () => void;
    /** used to persist inputs while switching between ChatId */
    readonly inputs: Map<ChatId, string | undefined>;
    setInput: (chatId: ChatId, input: string | undefined) => void;
    updateChatNeglection: (chatId: ChatId, updates: Partial<NeglectionState>) => void;
    getChatNeglection: (chatId: ChatId) => NeglectionState | undefined;
    clearChatNeglection: (chatId: ChatId) => void;
    updateChatAutopilot: (chatId: ChatId, updates: Partial<AutopilotState>) => void;
    getChatAutopilot: (chatId: ChatId) => AutopilotState | undefined;
    clearChatAutopilot: (chatId: ChatId) => void;
    updateCloseReason: (chatId: ChatId, reason: string) => void;
    getCloseReason: (chatId: ChatId) => string | undefined;
    updateSpellCheck: (chatId: ChatId, updates: Partial<SpellCheckState>) => void;
    getSpellCheck: (chatId: ChatId) => SpellCheckState | undefined;
    clearSpellCheck: (chatId: ChatId) => void;
    setChatRightBottomTab: (chatId: ChatId, tab: ChatRightBottomTab) => void;
    setChatRightTopTab: (chatId: ChatId, tab: RightTopTab) => void;
}

interface ChatMessagesState extends ChatMessagesCache {
    /** map of ChatId to messageId to message for fast lookups by messageId and reference stability. */
    readonly chats: Map<ChatId, Map<MessageId, ChatMessage>>;
    /**
     * Sorted arrays for efficient iteration and rendering.
     * Key: ChatId, Value: ChatMessage[] (sorted by index ascending).
     * ! Ensure this stays perfectly in sync with `chats` Map by using rebuildSortedArray when messages are added or removed or updated.
     */
    readonly sortedMessages: Map<ChatId, ChatMessage[]>;
    /** Map of ChatId to unread counts for fast lookups */
    readonly unreadCounts: Map<ChatId, number>;
}

/**
 * Add **only actions** that are directly used by the chat SDK to `ChatMessagesCache`.
 * Do not include any state in this type.
 * If you need to add actions not used directly by the chat SDK or any state,
 * consider using `ChatState` or `ChatMessagesState` instead.
 */
export interface ChatMessagesCache {
    addChatMessages: (chatId: ChatId, messages: ChatMessage[]) => void;
    /** Remove all state for a chat (messages, neglection, autopilot, inputs, etc.) */
    removeChat: (chatId: ChatId) => void;
    /**
     * Updates messages in the cache based on a predicate function.
     * Only iterates messages in the specified chat for optimal performance.
     *
     * @param shouldUpdate - Predicate function to determine which messages to update
     * @param update - Function to transform the message
     * @param chatId - Chat ID to update messages for (required for performance)
     *
     * @example
     * updateChatMessages(
     *   (message) => message.senderType === 'customer',
     *   (message) => ({ ...message, receiptStatus: 'read' }),
     *   'CHxxxxxx'
     * );
     */
    updateChatMessages: (
        shouldUpdate: (message: ChatMessage) => boolean,
        update: (message: ChatMessage) => ChatMessage,
        chatId: ChatId,
    ) => void;

    markCustomerIsTyping: (chatId: ChatId) => void;
    markCustomerStoppedTyping: (chatId: ChatId) => void;
}

/**
 * Helper to calculate unread customer message count from a sorted array.
 * Only counts unread customer messages from the most recent message backward.
 * Stops counting at the first read message
 */
function calculateUnreadCount(sortedMessages: ChatMessage[] | undefined): number {
    if (!sortedMessages || sortedMessages.length === 0) return 0;

    // Iterate backwards through sorted array
    let count = 0;
    for (let i = sortedMessages.length - 1; i >= 0; i--) {
        const message = sortedMessages[i];
        // Only count customer messages
        if (message.senderType !== "customer") continue;

        if (message.receiptStatus === "read") {
            break; // Stop at first read message (watermark)
        }
        count++;
    }

    return count;
}

/**
 * Helper to rebuild sorted array from Map, ensuring both structures stay in sync.
 * Only call when order might change (e.g., when messages are added or removed).
 */
function rebuildSortedArray(messagesMap: Map<MessageId, ChatMessage>): ChatMessage[] {
    return Array.from(messagesMap.values()).sort((a, b) => a.index - b.index);
}
export const useChatStore = createImmerStore<ChatMessagesState & ChatState>({
    persist: {
        name: "chat-store",
        partialize: (state) => ({
            chatNeglectionState: state.chatNeglectionState,
            chatAutopilotState: state.chatAutopilotState,
            closeReason: state.closeReason,
        }),
        storage: {
            getItem(name: string) {
                const str = localStorage.getItem(name);
                if (!str) {
                    return null;
                }
                const persisted = deserializeChatState(str);
                return {
                    state: persisted,
                } as StorageValue<PersistedChatState>;
            },
            setItem(name: string, value: StorageValue<PersistedChatState>) {
                const str = serializeChatState(value.state);
                localStorage.setItem(name, str);
            },
            removeItem(name: string) {
                localStorage.removeItem(name);
            },
        },
    },
    devtools: {
        store: "chat",
        name: "workspace/chat",
        isProd: import.meta.env.VITE_ENV_TYPE === "production",
        extra: {
            serialize: {
                options: {
                    map: true,
                },
            },
        },
    },
})((set, get) => ({
    //#region State Values
    activeChatId: undefined,
    typingCustomerChatIds: new Set<ChatId>(),
    chatNeglectionState: new Map<ChatId, NeglectionState>(),
    chatAutopilotState: new Map<ChatId, AutopilotState>(),
    chatSpellCheckState: new Map<ChatId, SpellCheckState>(),
    chats: new Map<ChatId, Map<MessageId, ChatMessage>>(),
    sortedMessages: new Map<ChatId, ChatMessage[]>(),
    unreadCounts: new Map<ChatId, number>(),
    inputs: new Map<ChatId, string | undefined>(),
    closeReason: new Map<ChatId, string>(),
    chatRightBottomTab: new Map<ChatId, ChatRightBottomTab>(),
    chatRightTopTab: new Map<ChatId, RightTopTab>(),
    //#endregion

    //#region Actions
    markCustomerIsTyping: (chatId: ChatId): void => {
        set(
            (state) => {
                state.typingCustomerChatIds.add(chatId);
            },
            false,
            "markCustomerIsTyping",
        );
    },
    markCustomerStoppedTyping: (chatId: ChatId): void => {
        set(
            (state) => {
                state.typingCustomerChatIds.delete(chatId);
            },
            false,
            "markCustomerStoppedTyping",
        );
    },
    setActiveChatId: (chatId: ChatId): void => {
        set(
            {
                activeChatId: chatId,
            },
            false,
            `setActiveChatId to ${chatId}`,
        );
    },
    clearActiveChatId: (): void => {
        set(
            {
                activeChatId: undefined,
            },
            false,
            "clearActiveChatId",
        );
    },
    updateChatNeglection: (chatId: ChatId, updates: Partial<NeglectionState>): void => {
        set(
            (state) => {
                const currentState = state.chatNeglectionState.get(chatId);
                state.chatNeglectionState.set(chatId, {
                    ...currentState,
                    ...updates,
                });
            },
            false,
            `updateChatNeglection for ${chatId}`,
        );
    },

    getChatNeglection: (chatId: ChatId): NeglectionState | undefined => {
        return get().chatNeglectionState.get(chatId);
    },

    clearChatNeglection: (chatId: ChatId): void => {
        set(
            (state) => {
                state.chatNeglectionState.delete(chatId);
            },
            false,
            `clearChatNeglection for ${chatId}`,
        );
    },

    updateChatAutopilot: (chatId: ChatId, updates: Partial<AutopilotState>): void => {
        set(
            (state) => {
                const currentState = state.chatAutopilotState.get(chatId);
                state.chatAutopilotState.set(chatId, {
                    ...currentState,
                    ...updates,
                });
            },
            false,
            `updateChatAutopilot for ${chatId}`,
        );
    },

    getChatAutopilot: (chatId: ChatId): AutopilotState | undefined => {
        return get().chatAutopilotState.get(chatId);
    },

    clearChatAutopilot: (chatId: ChatId): void => {
        set(
            (state) => {
                state.chatAutopilotState.delete(chatId);
            },
            false,
            `clearChatAutopilot for ${chatId}`,
        );
    },

    updateCloseReason: (chatId: ChatId, reason: string): void => {
        set(
            (state) => {
                state.closeReason.set(chatId, reason);
            },
            false,
            `updateCloseReason for ${chatId} to ${reason}`,
        );
    },

    getCloseReason: (chatId: ChatId): string | undefined => {
        return get().closeReason.get(chatId);
    },
    updateSpellCheck: (chatId: ChatId, updates: Partial<SpellCheckState>): void => {
        set(
            (state) => {
                const currentState = state.chatSpellCheckState.get(chatId) ?? {
                    status: "idle" as const,
                };
                state.chatSpellCheckState.set(chatId, {
                    ...currentState,
                    ...updates,
                });
            },
            false,
            `updateSpellCheck for ${chatId}`,
        );
    },

    getSpellCheck: (chatId: ChatId): SpellCheckState | undefined => {
        return get().chatSpellCheckState.get(chatId);
    },

    clearSpellCheck: (chatId: ChatId): void => {
        set(
            (state) => {
                state.chatSpellCheckState.delete(chatId);
            },
            false,
            `clearSpellCheck for ${chatId}`,
        );
    },

    addChatMessages: (chatId: ChatId, newMessages: ChatMessage[]): void => {
        set(
            (state) => {
                const messages = state.chats.get(chatId) ?? new Map<MessageId, ChatMessage>();
                newMessages.forEach((newMessage) => {
                    messages.set(newMessage.id, newMessage);
                });
                state.chats.set(chatId, messages);

                // Rebuild sorted array when messages are added
                const sorted = rebuildSortedArray(messages);
                state.sortedMessages.set(chatId, sorted);
                state.unreadCounts.set(chatId, calculateUnreadCount(sorted));
            },
            false,
            "addChatMessages",
        );
    },
    removeChat: (chatId: ChatId): void => {
        set(
            (state) => {
                void state.chats.delete(chatId);
                void state.sortedMessages.delete(chatId);
                void state.inputs.delete(chatId);
                void state.chatNeglectionState.delete(chatId);
                void state.unreadCounts.delete(chatId);
                void state.chatAutopilotState.delete(chatId);
                void state.closeReason.delete(chatId);
                void state.typingCustomerChatIds.delete(chatId);
                void state.chatSpellCheckState.delete(chatId);
                void state.chatRightBottomTab.delete(chatId);
                void state.chatRightTopTab.delete(chatId);
            },
            false,
            "removeChat",
        );
    },
    updateChatMessages: (
        shouldUpdate: (message: ChatMessage) => boolean,
        update: (message: ChatMessage) => ChatMessage,
        targetChatId: ChatId,
    ): void => {
        set(
            (state) => {
                const messages = state.chats.get(targetChatId);
                if (!messages) return;

                let hasUpdates = false;
                // Update in Map first
                for (const [messageId, message] of messages.entries()) {
                    if (shouldUpdate(message)) {
                        const updatedMessage = update(message);
                        messages.set(messageId, updatedMessage);
                        hasUpdates = true;
                    }
                }

                if (hasUpdates) {
                    // Rebuild sorted array from Map to sync references
                    //TODO: this is a performance bottleneck - but still a massive improvement over what we were doing for chat before, we can do even better.
                    const sorted = rebuildSortedArray(messages);
                    state.sortedMessages.set(targetChatId, sorted);
                    state.unreadCounts.set(targetChatId, calculateUnreadCount(sorted));
                }
            },
            false,
            "updateChatMessages",
        );
    },
    setInput: (chatId: ChatId, input: string | undefined): void => {
        set((state) => void state.inputs.set(chatId, input), false, `setInput for chatId: ${chatId}`);
    },
    setChatRightBottomTab: (chatId: ChatId, tab: ChatRightBottomTab): void => {
        set(
            (state) => void state.chatRightBottomTab.set(chatId, tab),
            false,
            `setChatRightBottomTab for ${chatId} to ${tab}`,
        );
    },
    setChatRightTopTab: (chatId: ChatId, tab: RightTopTab): void => {
        set(
            (state) => void state.chatRightTopTab.set(chatId, tab),
            false,
            `setChatRightTopTab for ${chatId} to ${tab}`,
        );
    },
    //#endregion
}));

//#region - Selectors for use in React components

/** Stable empty array reference to prevent infinite loops when using selectors. */
const EMPTY_MESSAGES_ARRAY: ChatMessage[] = [];

/**
 * Selector function for getting unread customer message count.
 * Returns a Zustand selector that can be used with useChatStore hook.
 * @example const unreadCount = useChatStore(selectUnreadCount(chatId));
 */
export const selectUnreadCount = (chatId: ChatId) => (state: ChatMessagesState & ChatState) =>
    state.unreadCounts.get(chatId) ?? 0;

/**
 * Selector function for getting sorted messages for a specific chat.
 * @example const messages = useChatStore(selectChatMessages(chatId));
 */
export const selectChatMessages = (chatId: ChatId) => (state: ChatMessagesState & ChatState) =>
    state.sortedMessages.get(chatId) ?? EMPTY_MESSAGES_ARRAY;

/**
 * Selector function for getting neglection state for a specific chat.
 * @example const neglectionState = useChatStore(selectChatNeglection(chatId));
 */
export const selectChatNeglection = (chatId: ChatId) => (state: ChatMessagesState & ChatState) =>
    state.chatNeglectionState.get(chatId);

/**
 * Selector function for getting autopilot state for a specific chat.
 * @example const autopilotState = useChatStore(selectChatAutopilot(chatId));
 */
export const selectChatAutopilot = (chatId: ChatId) => (state: ChatMessagesState & ChatState) =>
    state.chatAutopilotState.get(chatId);

/**
 * Selector function for getting close reason for a specific chat.
 * @example const closeReason = useChatStore(selectCloseReason(chatId));
 */
export const selectCloseReason = (chatId: ChatId) => (state: ChatMessagesState & ChatState) =>
    state.closeReason.get(chatId);

/**
 * Selector function for checking if customer is typing in a specific chat.
 * @example const isTyping = useChatStore(selectIsCustomerTyping(chatId));
 */
export const selectIsCustomerTyping = (chatId: ChatId) => (state: ChatMessagesState & ChatState) =>
    state.typingCustomerChatIds.has(chatId);

/**
 * Selector function for getting spellcheck state for a specific chat.
 * @example const spellCheckState = useChatStore(selectSpellCheck(chatId));
 */
export const selectSpellCheck = (chatId: ChatId) => (state: ChatMessagesState & ChatState) =>
    state.chatSpellCheckState.get(chatId);

/**
 * Selector function for getting the right-bottom tab selection for a specific chat.
 * Returns undefined if no selection has been saved.
 */
export const selectChatRightBottomTab = (chatId: ChatId | undefined) => (state: ChatMessagesState & ChatState) =>
    chatId ? state.chatRightBottomTab.get(chatId) : undefined;

export const selectChatRightTopTab = (chatId: ChatId | undefined) => (state: ChatMessagesState & ChatState) =>
    chatId ? state.chatRightTopTab.get(chatId) : undefined;

//#endregion - Selectors for use in React components

//#region - Persistence helpers
function serializeChatState(state: PersistedChatState): string {
    return JSON.stringify(state, (_key, value) => {
        if (value instanceof Map) {
            return { __type: "Map", value: Array.from(value.entries()) };
        }
        return value;
    });
}

function createEmptyPersistedState(): PersistedChatState {
    return {
        chatNeglectionState: new Map(),
        chatAutopilotState: new Map(),
        closeReason: new Map(),
    };
}

function deserializeChatState(str: string): PersistedChatState {
    try {
        return JSON.parse(str, (_key, value) => {
            if (value?.__type === "Map") {
                return new Map(value.value);
            }
            return value;
        }) as PersistedChatState;
    } catch {
        return createEmptyPersistedState();
    }
}
//#endregion - Persistence helpers
